import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import { MedisanteTargetSystem } from '../../../../pages/TargetSystems/types';
import Chip from '@material-ui/core/Chip';
import DialogContentText from '@material-ui/core/DialogContentText';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import React, {
  FunctionComponent,
  memo,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import { useManageContext } from '../../../../contexts/ManageContext/ManageContext';
import { isDemoOrg } from '../../../../contexts/AuthProvider/types';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import {
  notFullySuportedModel,
  onlyCareDevices,
} from '../../../../pages/TargetSystems/config';
import { Warning } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { UnsupportedHL7Types } from '../../../../pages/TargetSystems/components/EditTargetSystemDialog/provider';
import {
  useChangeTargetSystemMutation,
  useGetTargetSystemsQuery,
} from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: theme.spacing(0.25),
  },
  tooltip: {
    cursor: 'pointer',
  },
  select: {
    marginBottom: theme.spacing(4),
  },
}));

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;
  onClose?(): void;
  onCreated?(): void;
}

const ChangeTargetSystemsDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen, onClose, onCreated }) => {
    const classes = useStyles();
    const config = useConfig();
    const { authUser } = useAuthUser();
    const { enqueueSnackbar } = useSnackbar();

    const owner = devices[0].owner;
    const orgs = useMemo(() => [owner], [owner]);

    const { data: availableTargetSystems = [] } = useGetTargetSystemsQuery({
      apiBaseURL: config.apiBaseURL,
      orgs,
      filters: [],
    });

    const [changeTargetSystems, { isLoading }] =
      useChangeTargetSystemMutation();
    const [targetSystemIDs, setTargetSystemIDs] = useState<string[]>([]);

    const isDemo = isDemoOrg(authUser);

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const { dispatch } = useManageContext();

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
        dispatch({
          type: 'target-systems-changed',
        });
      }
    }, [onCreated, dispatch]);

    useEffect(() => {
      if (isOpen && devices.length > 0) {
        setTargetSystemIDs(devices[0].targetSystems);
      }
    }, [devices, isOpen]);

    const targetSystemIncompatible = availableTargetSystems
      .filter((ts) => {
        const notSupportedDevices = devices.filter(
          (d) => notFullySuportedModel.indexOf(d.model) !== -1
        );

        return (
          notSupportedDevices.length > 0 &&
          ts.format &&
          ts.format.match(/fhir/i)
        );
      })
      .map((ts) => ts.id);

    const targetSystemFromID = (id: string) => {
      let ts: MedisanteTargetSystem = {
        id,
        name: '',
        url: '',
        isDefault: false,
      };
      for (const s of availableTargetSystems) {
        if (s.id === id) {
          ts = s;
          break;
        }
      }
      return ts;
    };

    const devicesHasTheSameOwner = devices.every(
      (d) => d.owner === devices[0].owner
    );

    return (
      <>
        <Dialog
          data-testid="change-target-system-dialog"
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Change Webhooks</DialogTitle>
          <DialogContent style={{ minHeight: '230px' }}>
            <MedSelect
              itemDisabled={(o: MedisanteTargetSystem) => {
                return (
                  !o.isMutable &&
                  devices.some((d) => onlyCareDevices.indexOf(d.model) === -1)
                );
              }}
              name="target-systems"
              placeholder="Webhooks"
              multiple
              value={targetSystemIDs}
              options={availableTargetSystems}
              onChange={(e) => {
                // @ts-ignore
                setTargetSystemIDs(e.target.value);
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                  },
                },
                anchorOrigin: { vertical: -25, horizontal: 'left' },
                getContentAnchorEl: null,
              }}
              valueGetter={(o) => o.id}
              optionTextGetter={(o) => {
                if (targetSystemIncompatible.indexOf(o.id) !== -1) {
                  return (
                    <>
                      <Tooltip
                        title={`Devices and/or measurements may not be supported wholly or partly`}
                      >
                        <Warning htmlColor={'rgb(250, 84, 54)'} />
                      </Tooltip>
                      {o.name}
                    </>
                  );
                } else {
                  return o.name;
                }
              }}
              disabled={!devicesHasTheSameOwner}
              renderValue={(selectedTargetSystems) => {
                return (
                  <div className={classes.chips}>
                    {(selectedTargetSystems as string[])
                      .map((id) => targetSystemFromID(id))
                      .map((ts) => (
                        <Tooltip
                          key={ts.id}
                          className={classes.tooltip}
                          title={ts.url}
                        >
                          <Chip
                            label={ts.name}
                            className={classes.chip}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onDelete={(e) => {
                              setTargetSystemIDs(
                                targetSystemIDs.filter((id) => id !== ts.id)
                              );
                            }}
                          />
                        </Tooltip>
                      ))}
                  </div>
                );
              }}
            />

            {targetSystemIDs.length === 0 && (
              <>
                <DialogContentText>
                  Measurements from devices without any webhook assigned will be
                  lost.
                </DialogContentText>
              </>
            )}
            <br />

            {!devicesHasTheSameOwner && (
              <>
                <DialogContentText>
                  All devices must have the same user.
                </DialogContentText>
              </>
            )}
            <br />
            {targetSystemIncompatible.find(
              (id) => targetSystemIDs.indexOf(id) !== -1
            ) && (
              <Alert severity="warning" style={{ marginTop: '1em' }}>
                Not all devices and/or measurement types are supported,
                including (but not limited to):
                <div style={{ marginTop: '1em' }}>
                  {UnsupportedHL7Types.length ? (
                    <>
                      <strong>Measurement Types:</strong>
                      <ul>
                        {UnsupportedHL7Types.map((type) => (
                          <li key={type}>{type}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              disabled={isDemo && !devicesHasTheSameOwner}
              onClick={() => {
                changeTargetSystems({
                  apiBaseURL: config.apiBaseURL,
                  orgIDs:
                    authUser.selectedOrgs.length === 1
                      ? authUser.selectedOrgs
                      : authUser.orgs.map((o) => o.id),
                  devices,
                  targetSystemIDs,
                }).then((resp: any) => {
                  if (!resp.error) {
                    handleClose();
                    handleCreated();
                    return;
                  }
                  enqueueSnackbar(parseErrorMessage(resp.error), {
                    variant: 'error',
                  });
                });
              }}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default ChangeTargetSystemsDialog;
